import {
  buildGuaranteesApplicationPath,
  getStringValue,
  isGuaranteesClientInitialSigningStatus,
  isGuaranteesClientSigningStatus,
} from '~/modules/guarantees/utils.ts';
import { DocStatusEnum, type TDocument } from '~/modules/identification/forms/Documents/types.ts';
import {
  type GuaranteesApplicationStatus,
  GuaranteesFileType,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME } from './constants.ts';
import type { TClientGeneratedDocumentRow, TClientUploadDocumentRow } from './types.ts';

export const getClientStep2RedirectPath = ({
  application,
  contragentId,
  effectiveApplicationId,
  normalizedStatus,
}: {
  application: unknown;
  contragentId: string;
  effectiveApplicationId: string;
  normalizedStatus?: GuaranteesApplicationStatus | string | null;
}): string => {
  if (!contragentId) {
    return '/guarantees/get-started';
  }

  if (application && !isGuaranteesClientSigningStatus(normalizedStatus)) {
    return buildGuaranteesApplicationPath(effectiveApplicationId, contragentId, false);
  }

  return '';
};

export const getClientStep2ActionsDisabled = ({
  application,
  normalizedStatus,
}: {
  application: unknown;
  normalizedStatus?: GuaranteesApplicationStatus | string | null;
}): boolean => !!application && !isGuaranteesClientInitialSigningStatus(normalizedStatus);

export const buildClientDocumentMenuId = (documentId: string) =>
  `guarantees-client-file-${documentId}`;
export const mapApplicationFilesToClientDocumentRows = (
  files: TGuaranteesApplicationFile[] = []
): TClientUploadDocumentRow[] =>
  files.reduce<TClientUploadDocumentRow[]>((rows, file) => {
    if (file.fileType !== GuaranteesFileType.CLIENT_FOUNDING_DOCUMENTS_AND_GOVERNANCE_INFO) {
      return rows;
    }

    const applicationFileId = getStringValue(file.id);
    const fileId = getStringValue(file.fileId);

    if (!applicationFileId && !fileId) {
      return rows;
    }

    rows.push({
      id: applicationFileId || fileId,
      applicationFileId,
      fileId,
      fileName: getStringValue(file.description) || CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME,
    });

    return rows;
  }, []);

export const getWarrantyStatementDocumentFromApplicationFiles = (
  files: TGuaranteesApplicationFile[] = []
): TClientGeneratedDocumentRow | null => {
  const warrantyStatementFile = files.find(
    (file) => file.fileType === GuaranteesFileType.WARRANTY_STATEMENT
  );

  if (!warrantyStatementFile) {
    return null;
  }

  const applicationFileId = getStringValue(warrantyStatementFile.id);
  const fileId = getStringValue(warrantyStatementFile.fileId);

  if (!applicationFileId && !fileId) {
    return null;
  }

  return {
    id: applicationFileId || fileId,
    fileId,
    fileName:
      getStringValue(warrantyStatementFile.description) || CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME,
  };
};

export const buildClientStep2SigningDocuments = (
  applicationFiles: TGuaranteesApplicationFile[] = []
): { fileIds: string[]; documents: TDocument[] } => {
  const signingDocuments: Array<{ fileId: string; fileName?: string }> = [];

  applicationFiles.forEach((file) => {
    const fileId = getStringValue(file.fileSignedId || file.fileId);

    if (!fileId) {
      return;
    }

    signingDocuments.push({
      fileId,
      fileName: getStringValue(file.description),
    });
  });

  const uniqueSigningDocuments = Array.from(
    new Map(signingDocuments.map((document) => [document.fileId, document])).values()
  );

  return {
    fileIds: uniqueSigningDocuments.map((document) => document.fileId),
    documents: uniqueSigningDocuments.map((document, index) => ({
      id: document.fileId,
      nameDoc: document.fileName || `document_${index + 1}`,
      idDictWebDocument: index + 1,
      idDoc: index + 1,
      docStatus: [DocStatusEnum.NOT_VERIFIED],
      documentName: document.fileName || `document_${index + 1}`,
    })),
  };
};

export const getClientStep2SigningDocumentPreviewFileId = (
  documents: TDocument[],
  documentId: number | string
): string | undefined =>
  getStringValue(documents.find((document) => String(document.idDoc) === String(documentId))?.id) ||
  undefined;
